import { useCallback, useEffect, useState } from 'react';
import { getSocket } from '../../../socket';

const EMPTY = { active: false, startedAt: null, availableUntil: null, role: 'viewer', canSeeAll: false, canSeeDrafts: false, ownPageIds: [], pages: [], mine: null, reviews: [] };

export default function useTableTopics({ meetingId, authUser }) {
  const url = process.env.REACT_APP_URL;
  const token = authUser?.token;
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!meetingId || !token) {
      setData(EMPTY);
      return;
    }
    try {
      const response = await fetch(`${url}/meeting/${meetingId}/table-topics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) setData({ ...EMPTY, ...(await response.json()) });
    } catch {
      // Table Topics reviews are optional; the agenda keeps working without them.
    }
  }, [meetingId, token, url]);

  const save = useCallback(async (entries) => {
    setSaving(true);
    try {
      const response = await fetch(`${url}/meeting/${meetingId}/table-topics/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entries }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.Error || response.statusText);
      setData({ ...EMPTY, ...body });
      return body;
    } finally {
      setSaving(false);
    }
  }, [meetingId, token, url]);

  const publish = useCallback(async ({ agendaItemIds, publishAll = false }) => {
    setSaving(true);
    try {
      const response = await fetch(`${url}/meeting/${meetingId}/table-topics/review/publish`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ agendaItemIds, publishAll }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.Error || response.statusText);
      setData({ ...EMPTY, ...body });
      return body;
    } finally {
      setSaving(false);
    }
  }, [meetingId, token, url]);

  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    if (!meetingId) return undefined;
    const socket = getSocket();
    const matches = payload => String(payload?.meetingId || '') === String(meetingId);
    const handleChange = payload => { if (matches(payload)) void reload(); };
    socket.on('meeting:table-topics-session', handleChange);
    socket.on('meeting:table-topics-reviews-changed', handleChange);
    return () => {
      socket.off('meeting:table-topics-session', handleChange);
      socket.off('meeting:table-topics-reviews-changed', handleChange);
    };
  }, [meetingId, reload]);

  // Close the window locally when the 3.5 hours run out.
  useEffect(() => {
    if (!data.active || !data.availableUntil) return undefined;
    const remaining = new Date(data.availableUntil).getTime() - Date.now();
    const timeout = setTimeout(() => setData(current => ({ ...current, active: false })), Math.max(remaining, 0));
    return () => clearTimeout(timeout);
  }, [data.active, data.availableUntil]);

  return { ...data, saving, reload, save, publish };
}
